import { useDispatch, useSelector } from "react-redux";
import { DataGrid, GridCellParams, GridColDef, GridColumnHeaderParams, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Button, Grid } from "@mui/material";
import { IConfigVersion, IState, IStructuredError, ModalDialog } from "store/types";
import { useCallback, useState } from "react";
import { styled } from "styled-components";
import { updateConfig } from "actions/config";
import moment from "moment";
import { AdminSectionDescription, AdminSectionTitle } from "./ApplicationSettings";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { RulesUploadDialog } from "./RulesUploadDialog";
import { sortBy } from "lodash";
import { RulesListContextMenu } from "./RulesListContextMenu";
import Spinner from "components/spinner";
import { useNavigate } from "@reach/router";
import { RuleErrorsDialog } from "./rules/RuleErrorsDialog";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

export const ActionMenuText = styled.p`
  margin-top: 0px;
  margin-bottom: 0px;
  line-height: 20px;
  font-size: 14px;
  word-wrap: break-word;
`;

const ActionButton = styled(Button)`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  padding: 12px 16px;
  margin-top: 64px;
`;

export const RulesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // What we need from Redux
  const config = useSelector((state: IState) => state.config);
  const submittingChanges = useSelector((state: IState) => state.submittingChanges);
  const loading = useSelector((state: IState) => state.loadingConfig);

  const getLastVersionNumber = useCallback(() => {
    if (config && config.versions) {
      const sortedVersionsDesc = sortBy(config.versions, "version");
      const sortedVersionsAsc = sortedVersionsDesc.reverse();
      return sortedVersionsAsc[0].version;
    }
    return 0;
  }, [config]);

  const [openModal, setOpenModal] = useState(ModalDialog.NONE);
  const handleCloseModal = useCallback(() => setOpenModal(ModalDialog.NONE), []);
  const handleOpenModal = useCallback(() => setOpenModal(ModalDialog.UPLOAD), []);

  const [uploadResponse, setUploadResponse] = useState<IStructuredError[]>([]);
  const captureUploadResponse = useCallback((errors: IStructuredError[]) => {
    setUploadResponse(errors);
    setOpenModal(ModalDialog.ERRORS);
  }, []);

  const handleSetActive = useCallback(
    (version: number) => {
      dispatch(updateConfig({ ...config, currentVersion: version }));
    },
    [config, dispatch],
  );

  const renderActiveVersion = (params: GridRenderCellParams) => {
    return params.row.isCurrentVersion ? (
      <div style={{ backgroundColor: "#cccccc", padding: "8px 8px", textAlign: "center", justifyContent: "right" }}>
        Active version
      </div>
    ) : (
      <div />
    );
  };

  const renderContextMenu = (params: GridRenderCellParams) => {
    return (
      <RulesListContextMenu
        version={params.row.version}
        isCurrentVersion={params.row.isCurrentVersion}
        onSetActive={handleSetActive}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "version",
      headerName: "Version",
      flex: 0.5,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerClassName: "rules-admin--header",
      renderHeader: (_params: GridColumnHeaderParams) => <strong>{"Version"}</strong>,
    },
    {
      field: "timestamp",
      headerName: "Updated",
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerClassName: "rules-admin--header",
      renderHeader: (_params: GridColumnHeaderParams) => <strong>{"Updated"}</strong>,
    },
    {
      field: "user",
      headerName: "Updated by",
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerClassName: "rules-admin--header",
      renderHeader: (_params: GridColumnHeaderParams) => <strong>{"Updated by"}</strong>,
    },
    {
      field: "isCurrentVersion",
      headerName: "",
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "right",
      headerClassName: "rules-admin--header",
      renderCell: renderActiveVersion,
    },
    {
      field: "contextMenu",
      headerName: "",
      flex: 0.5,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "right",
      headerClassName: "rules-admin--header",
      renderCell: renderContextMenu,
    },
  ];

  const sortRowsByVersionAsc = useCallback(() => {
    if (config && config.versions) {
      const sorted = sortBy(config.versions, "version");
      const reversed = sorted.reverse();
      return reversed;
    }
    return [];
  }, [config]);

  const rows =
    config && config.versions
      ? sortRowsByVersionAsc().map((version: IConfigVersion) => ({
          id: version.version,
          version: version.version,
          timestamp: moment(version.timestamp).format("h:mma MMMM D YYYY"),
          user: version.userToken,
          isCurrentVersion: version.version === config.currentVersion,
        }))
      : [];

  const onEditRule = useCallback(
    (params: GridCellParams) => {
      // Don't open the rules editor if we've clicked on the menu icon
      if (params.field !== "contextMenu") {
        navigate(`/admin/rule/${params.row.version}?active=${params.row.isCurrentVersion}`);
      }
    },
    [navigate],
  );

  if (submittingChanges || loading) {
    return <Spinner loading={true} />;
  } else
    return (
      <>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} style={{ display: "flex", justifyContent: "left" }}>
            <AdminSectionTitle>Rule versions</AdminSectionTitle>
          </Grid>
          <Grid item xs={6} style={{ display: "flex", justifyContent: "right" }}>
            <ActionButton variant="outlined" startIcon={<FileUploadIcon />} onClick={handleOpenModal}>
              Upload CSV
            </ActionButton>
          </Grid>
        </Grid>
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ marginRight: "6px", lineHeight: "1" }}>
            <ErrorOutlineIcon style={{ fontSize: "large", marginTop: 11, paddingRight: 0, color: "#252525" }} />
          </div>
          <div>
            <AdminSectionDescription>
              Click a Rule version row to view its rules and download them as CSV. Only the latest set of Rules can be
              modified. For details on creating rules, refer to the LMSMorph guide.
            </AdminSectionDescription>
          </div>
        </div>
        <Box
          sx={{
            maxWidth: "100%",
            paddingTop: "30px",
            "& .Mui-odd": {
              backgroundColor: "var(--neutral-100, #FAFAFA)",
            },
            "& .rules-admin--header": {
              border: "0px 0px 1px 0px",
              gap: "8px",
              backgroundColor: "var(--neutral-200, #eee)",
            },
          }}
        >
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            sx={{
              "& .MuiDataGrid-cell": {
                cursor: "pointer",
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            onCellClick={onEditRule}
          ></DataGrid>
        </Box>
        <RulesUploadDialog
          open={openModal === ModalDialog.UPLOAD}
          onClose={handleCloseModal}
          onConfirm={handleCloseModal}
          onError={captureUploadResponse}
          lastVersion={getLastVersionNumber()}
        />
        <RuleErrorsDialog open={openModal === ModalDialog.ERRORS} onClose={handleCloseModal} errors={uploadResponse} />
      </>
    );
};
